/**
 * Guest-mode routing (SCRUM-275; owner decision 2026-10-07: `/` opens the
 * chat for every visitor, logged in or not — the landing stays, at `/about`).
 *
 *   /         HomeRoute          member → /chat (+search); guest mode → the
 *                                chat in guest mode (guests live at `/`,
 *                                ChatGPT-style); otherwise → /about
 *   /chat     ChatEntryRoute     member → the chat; anonymous + guest mode →
 *                                `/` (+search); anonymous otherwise →
 *                                /login?return_to
 *   /settings SettingsEntryRoute member → SettingsScreen; a visitor in guest
 *                                mode → the guest «Ещё» (SCRUM-276);
 *                                otherwise → /login?return_to
 *
 * "Guest mode" for a visitor = `guest.enabled` OR a live guest session in
 * this browser (contract issue C5): switching guest mode off blocks new
 * messages, but someone mid-conversation can still read it and sign in to
 * keep it. While the boot refresh or the config is in flight the shared
 * pending spinner shows — deciding early would bounce a signed-in member.
 *
 * Config-fetch failure (fix round 1, same C5 "nobody loses a conversation"):
 * a visitor who already has a live session keeps guest mode ON through a
 * fail-closed config (`FAIL_CLOSED_GUEST_CONFIG` — the same shape the
 * server's own omitted fields produce) rather than being evicted to `/about`
 * or the login door just because the network hiccupped. A visitor with no
 * session sees today's behaviour on error: no guest mode. Either way, an
 * in-flight retry (`retryOnMount`, the default) after an earlier failure
 * counts as still pending, not a settled error — otherwise every later
 * mount would bounce immediately while that retry runs in the background.
 */
import { useEffect, type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { AuthBootstrapPending } from "@/app/RequireAuth";
import { ChatRoute } from "@/features/chat/ChatRoute";
import {
  FAIL_CLOSED_GUEST_CONFIG,
  useGuestConfig,
  type GuestConfig,
} from "@/features/chat/guest/api";
import {
  setGuestPointerRetentionDays,
  useGuestPointer,
} from "@/features/chat/guest/pointer";
import { GuestMoreScreen } from "@/features/settings/GuestMoreScreen";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";

export interface GuestModeState {
  isMember: boolean;
  /** Anonymous and still waiting for the boot refresh or the guest config. */
  pending: boolean;
  /** The config when guest mode applies to this visitor; null otherwise. */
  config: GuestConfig | null;
}

export function useGuestMode(): GuestModeState {
  const isMember = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const pointer = useGuestPointer();
  const query = useGuestConfig({ enabled: !isMember });
  const data = query.data;

  // The pointer's TTL follows the configured retention — real data only,
  // never the fail-closed fallback below (fix round 1).
  useEffect(() => {
    if (data !== undefined) setGuestPointerRetentionDays(data.retention_days);
  }, [data]);

  // A fetch attempt is actively in flight with nothing to show yet. Checked
  // directly instead of relying on `query.isPending` alone: once the query
  // has ever errored, `status` stays "error" even while `retryOnMount` (the
  // default) refetches in the background, so `isPending` never flips back on
  // — every later mount would otherwise bounce immediately instead of
  // waiting for that in-flight attempt to resolve (fix round 1).
  const resolving = data === undefined && query.fetchStatus === "fetching";

  // Contract amendment C5: nobody loses a conversation. A config fetch that
  // settles into an outright error must not evict a visitor already
  // mid-conversation in this browser — fall back to the same fail-closed
  // shape the server's own omitted fields produce (fix round 1). A visitor
  // with no live session keeps today's behaviour on error: no guest mode.
  const hasSession = pointer?.hasSession === true;
  const effective =
    data ?? (query.isError && hasSession ? FAIL_CLOSED_GUEST_CONFIG : undefined);

  return {
    isMember,
    pending: !isMember && (!bootstrapped || resolving),
    config: effective !== undefined && (effective.enabled || hasSession) ? effective : null,
  };
}

/*
 * The two chat redirects below carry `location.state` along (final web fix
 * round, item 1): an "Ask the assistant about this" link (ArticleScreen,
 * SellerDetail) navigates to `/chat` with `state: { seed }`, and ChatRoute
 * prefills the composer from it. A `<Navigate>` without `state` dropped the
 * seed on the hop — `/chat` → `/` for an anonymous visitor in guest mode,
 * `/` → `/chat` for a member — so the composer opened empty. The `/about`
 * and login redirects carry nothing: neither screen reads a seed.
 */
export function HomeRoute(): ReactElement {
  const { isMember, pending, config } = useGuestMode();
  const location = useLocation();
  if (isMember) return <Navigate to={"/chat" + location.search} replace state={location.state} />;
  if (pending) return <AuthBootstrapPending />;
  if (config !== null) return <ChatRoute guestConfig={config} />;
  return <Navigate to="/about" replace />;
}

export function ChatEntryRoute(): ReactElement {
  const { isMember, pending, config } = useGuestMode();
  const location = useLocation();
  if (isMember) return <ChatRoute />;
  if (pending) return <AuthBootstrapPending />;
  if (config !== null) {
    return <Navigate to={"/" + location.search} replace state={location.state} />;
  }
  // Guest mode off: exactly what RequireAuth did (STORY-002-009 AC12).
  const params = new URLSearchParams({ return_to: location.pathname + location.search });
  return <Navigate to={"/login?" + params.toString()} replace />;
}

/**
 * SettingsEntryRoute — "/settings", the «Ещё» tab (SCRUM-276). A member gets
 * the real settings slip; a visitor in guest mode (`useGuestMode`'s C5
 * fail-closed rule included — a live session keeps the guest «Ещё» even with
 * guest mode toggled off) gets the guest counterpart instead; anonymous with
 * guest mode off keeps today's /login?return_to redirect byte-for-byte.
 */
export function SettingsEntryRoute(): ReactElement {
  const { isMember, pending, config } = useGuestMode();
  const location = useLocation();
  if (isMember) return <SettingsScreen />;
  if (pending) return <AuthBootstrapPending />;
  if (config !== null) return <GuestMoreScreen />;
  // Guest mode off: exactly what RequireAuth did.
  const params = new URLSearchParams({ return_to: location.pathname + location.search });
  return <Navigate to={"/login?" + params.toString()} replace />;
}
